import { useState } from 'react'
import { supabase } from '../../supabase'
import { useAuth } from '../../contexts/AuthContext'
import { TAUX_TVA, TAUX_REMUNERATION, SEUIL_MINIMUM } from '../../utils/calculs'
import { tarifAccueilPour } from '../../utils/tarifsJournee'
import { fetchAll } from '../../utils/planning'
import {
  detecterFormat, parseBooker, parseSpa, agreger, matchSuperviseur,
} from '../../utils/importVentes'

const LIBELLE_FORMAT = { booker: 'Spa Booker (Orders Report)', spa: 'App spa (Détail des commandes)' }

export default function ImportCSV({ onClose }) {
  const { profile } = useAuth()
  const [, setFile] = useState(null)
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [msg, setMsg] = useState(null)
  const [progress, setProgress] = useState(null)

  async function handleFile(e) {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setMsg(null)
    setPreview(null)
    setLoading(true)

    const text = await f.text()
    const format = detecterFormat(text)
    if (!format) {
      setMsg({ type: 'error', text: "Format non reconnu. Attendu : « Orders Report » de Spa Booker, ou « Détail des commandes » de l'app spa." })
      setLoading(false)
      return
    }

    let lu
    try {
      lu = format === 'spa' ? parseSpa(text) : parseBooker(text)
    } catch (err) {
      setMsg({ type: 'error', text: err.message })
      setLoading(false)
      return
    }

    // Charger les superviseurs depuis la BDD
    const { data: sups } = await supabase.from('profiles')
      .select('id, prenom, nom').eq('role', 'superviseur').eq('actif', true)

    // Charger les tarifs actifs
    const { data: tarifs } = await supabase.from('tarifs')
      .select('id, pole, prix, prix_reference, actif, label').eq('actif', true)

    // Grouper par (date, pole) — superviseur peut être différent pour SPA et Hydro
    const jours = agreger(lu.rows, tarifs, { superviseurPremierNonVide: format === 'spa' })
    const warnings = [...lu.avertissements]

    // JPO sans tarif gratuit valorisé : la rémunération du superviseur ne les prendra pas en compte
    for (const j of jours) {
      if (j.jposSansValorisation > 0) warnings.push(`${j.date} ${j.pole} : ${j.jposSansValorisation} JPO sans tarif gratuit valorisé (prix de référence à renseigner dans Soins & Tarifs) : rémunération non valorisée`)
    }

    // Superviseurs et accueil
    if (format === 'booker') {
      // Booker : superviseur (colonne Superviseur) et accueil (OrderClosedBy) lus dans le CSV
      for (const j of jours) {
        j.superviseurObj = matchSuperviseur(j.superviseurNom, sups || [])
        if (!j.superviseurObj) warnings.push(`${j.date} ${j.pole} : superviseur "${j.superviseurNom}" non trouvé`)
        if (j.superviseursVus.length > 1) warnings.push(`${j.date} ${j.pole} : plusieurs superviseurs dans le fichier (${j.superviseursVus.join(', ')}), "${j.superviseurNom}" retenu`)
        j.accueilObj = matchSuperviseur(j.accueilNom, sups || [])
        if (!j.accueilObj && j.accueilNom) warnings.push(`${j.date} : accueil "${j.accueilNom}" non trouvé (ignoré)`)
      }
    } else if (jours.length > 0) {
      // App spa : superviseur SPA, superviseur HYDRO et accueil viennent du planning (affectations).
      // « Encaissé par » n'est pas l'accueil, et la colonne Superviseur du CSV sert seulement de contrôle.
      const { data: aff, error: errAff } = await fetchAll(() => supabase.from('affectations')
        .select('date, type, profil:superviseur_id(id, prenom, nom)')
        .in('type', ['ACCUEIL', 'SUPERVISION', 'HYDRO']).in('statut', ['prevu', 'valide']).not('superviseur_id', 'is', null)
        .gte('date', jours[0].date).lte('date', jours[jours.length - 1].date)
        .order('date').order('type'))
      if (errAff) warnings.push(`Planning illisible : ${errAff.message}. Aucune séance ne pourra être importée.`)
      const planning = {}
      for (const a of aff || []) if (a.profil) planning[`${a.date}_${a.type}`] = a.profil
      const sansAccueil = new Set()
      for (const j of jours) {
        const poste = j.pole === 'HYDRO' ? 'HYDRO' : 'SUPERVISION'
        j.superviseurObj = planning[`${j.date}_${poste}`] || null
        j.accueilObj = planning[`${j.date}_ACCUEIL`] || null
        if (!errAff) {
          if (!j.superviseurObj) warnings.push(`${j.date} ${j.pole} : aucun superviseur au planning${j.superviseurNom ? ` (le CSV indique "${j.superviseurNom}")` : ''}, séance ignorée`)
          else if (j.superviseurNom) {
            const duCsv = matchSuperviseur(j.superviseurNom, sups || [])
            if (!duCsv || duCsv.id !== j.superviseurObj.id) warnings.push(`${j.date} ${j.pole} : le CSV indique "${j.superviseurNom}", le planning ${j.superviseurObj.prenom} ${j.superviseurObj.nom} (planning retenu)`)
          }
          if (!j.accueilObj) sansAccueil.add(j.date)
        }
      }
      for (const d of sansAccueil) warnings.push(`${d} : aucun accueil au planning (journée d'accueil non créée)`)
    }

    // Séances déjà en base sur la période : l'import les écrasera
    if (jours.length > 0) {
      const { data: existantes } = await fetchAll(() => supabase.from('seances')
        .select('id, date, pole, ca_ttc, total_massage_payant, total_massage_gratuit, cloture')
        .gte('date', jours[0].date).lte('date', jours[jours.length - 1].date)
        .order('date').order('pole').order('id'))
      const parCle = {}
      for (const s of existantes || []) parCle[`${s.date}_${s.pole}`] = s
      for (const j of jours) j.existante = parCle[`${j.date}_${j.pole}`] || null
    }

    // Format spa : un jour de soin hors de la période de paiement du fichier est probablement incomplet
    if (lu.periode) {
      for (const j of jours) j.partielle = j.date < lu.periode.min || j.date > lu.periode.max
    }

    const nbEcrasees = jours.filter(j => j.existante && !j.existante.cloture).length
    if (nbEcrasees > 0) warnings.unshift(`${nbEcrasees} séance(s) déjà en base seront écrasées (colonne « En base »). Vérifie qu'elles ne viennent pas d'une autre source ou d'un export partiel.`)
    const nbPartielles = jours.filter(j => j.partielle).length
    if (nbPartielles > 0) warnings.push(`${nbPartielles} séance(s) dont la date de soin est hors de la période de paiement du fichier : probablement incomplètes (⚠ dans le tableau).`)

    setPreview({ jours, warnings, sups, tarifs, format })
    setLoading(false)
  }

  async function handleImport() {
    if (!preview) return
    setImporting(true)
    setMsg(null)

    const { jours, tarifs } = preview
    let ok = 0, skip = 0, errors = []

    // Le tarif d'accueil dépend de la date (standard ou majoré) : calculé à chaque journée

    // Collecter les dates uniques pour l'accueil (une seule fois par date)
    const accueilParDate = {}

    for (let i = 0; i < jours.length; i++) {
      const j = jours[i]
      setProgress({ current: i + 1, total: jours.length, date: j.date })

      if (!j.superviseurObj) { skip++; continue }

      const ca_ttc = j.ca
      const ca_ht = parseFloat((ca_ttc / TAUX_TVA).toFixed(2))
      // Portes ouvertes : hors CA, mais valorisées pour la rémunération du superviseur (comme Saisie.js)
      const base_ht = parseFloat(((ca_ttc + j.valorisation) / TAUX_TVA).toFixed(2))
      const rem_reelle = parseFloat((base_ht * TAUX_REMUNERATION).toFixed(2))
      const rem_affichee = Math.round(Math.max(rem_reelle, SEUIL_MINIMUM))

      const { data: existing } = await supabase.from('seances')
        .select('id, cloture').eq('date', j.date).eq('pole', j.pole).maybeSingle()

      if (existing?.cloture) { skip++; continue }

      const payload = {
        date: j.date, pole: j.pole,
        superviseur_id: j.superviseurObj.id, superviseur2_id: null,
        cree_par: profile.id,
        total_massage_payant: j.payants,
        total_massage_gratuit: j.gratuits,
        total_massage: j.payants + j.gratuits,
        ca_ttc, ca_ht, remuneration_reelle: rem_reelle, remuneration_affichee: rem_affichee,
        nb_sde_40:0, nb_sde_35:0, nb_sde_33:0, nb_sde_30:0, nb_sde_29:0, nb_sde_20:0,
        nb_vip_gratuit:0, nb_sup:0, nb_eleves:0,
        nb_hydro_60:0, nb_hydro_55:0, nb_hydro_35:0, nb_hydro_33:0, nb_hydro_30:0,
        nb_hydro_29:0, nb_hydro_vip:0,
      }

      let seanceId
      if (existing) {
        await supabase.from('seances').update(payload).eq('id', existing.id)
        await supabase.from('seance_soins').delete().eq('seance_id', existing.id)
        seanceId = existing.id
      } else {
        const { data, error } = await supabase.from('seances').insert(payload).select().single()
        if (error) { errors.push(`${j.date} ${j.pole} : ${error.message}`); continue }
        seanceId = data.id
      }

      const soinsPayload = Object.entries(j.soins).map(([tarif_id, quantite]) => {
        const t = tarifs.find(x => x.id === tarif_id)
        return { seance_id: seanceId, tarif_id, label: t?.label || `Prix ${t?.prix}€`, prix: t?.prix || 0, quantite, total: quantite * (t?.prix || 0), gratuit: (t?.prix || 0) === 0 }
      })
      if (soinsPayload.length > 0) await supabase.from('seance_soins').insert(soinsPayload)
      ok++

      // Accueil : une seule fois par date
      if (j.accueilObj && !accueilParDate[j.date]) {
        accueilParDate[j.date] = true
        let exA
        if (preview.format === 'spa') {
          // Planning = source : si une journée d'accueil existe déjà ce jour-là (saisie manuelle), on n'y touche pas
          const { data: ligneJour } = await supabase.from('seances_accueil')
            .select('id, cloture').eq('date', j.date).limit(1)
          exA = ligneJour && ligneJour.length > 0 ? ligneJour[0] : null
        } else {
          const { data } = await supabase.from('seances_accueil')
            .select('id, cloture').eq('date', j.date).eq('superviseur_id', j.accueilObj.id).maybeSingle()
          exA = data
        }
        if (!exA && !exA?.cloture) {
          const { montant: montantAccueil } = await tarifAccueilPour(j.date)
          if (montantAccueil === null) {
            errors.push(`${j.date} accueil : aucun tarif trouvé (Soins & Tarifs, onglet Journées)`)
          } else {
            await supabase.from('seances_accueil').insert({
              date: j.date, superviseur_id: j.accueilObj.id,
              montant: montantAccueil, cree_par: profile.id
            })
          }
        }
      }
    }

    setProgress(null)
    setImporting(false)
    setMsg({
      type: errors.length > 0 ? 'warning' : 'success',
      text: `Import terminé : ${ok} séance${ok > 1 ? 's' : ''} importée${ok > 1 ? 's' : ''}${skip > 0 ? `, ${skip} ignorée${skip > 1 ? 's' : ''}` : ''}${errors.length > 0 ? `. Erreurs : ${errors.join(', ')}` : ''}.`
    })
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{maxWidth:700, maxHeight:'85vh', overflowY:'auto'}}>
        <div className="modal-header">
          <div>
            <h3>Import des ventes</h3>
            <p className="modal-sub">Spa Booker ou app spa : le format est détecté automatiquement</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {msg && <div className={`alert alert-${msg.type}`}>{msg.text}</div>}

        {!preview && (
          <div>
            <p style={{fontSize:13, color:'var(--stone)', marginBottom:16, lineHeight:1.6}}>
              Importe le rapport « Orders Report » de Spa Booker ou le rapport « Détail des commandes » de l'app spa.
              L'import crée les séances SPA, Hydro et Accueil pour chaque jour. Avec l'app spa, superviseurs et accueil sont lus dans le planning.
            </p>
            <div className="form-group">
              <label>Fichier CSV</label>
              <input type="file" accept=".csv" onChange={handleFile}
                style={{padding:'8px', border:'1px solid var(--border)', borderRadius:'var(--radius)', fontSize:13}} />
            </div>
            {loading && <div className="spinner" />}
          </div>
        )}

        {preview && !msg && (
          <div>
            <div className="alert alert-info" style={{marginBottom:16}}>
              Format détecté : <strong>{LIBELLE_FORMAT[preview.format]}</strong>.{' '}
              {preview.jours.length} séance{preview.jours.length > 1 ? 's' : ''} (date, pôle) trouvée{preview.jours.length > 1 ? 's' : ''} dans le fichier.
            </div>

            {preview.warnings.length > 0 && (
              <div className="alert alert-warning" style={{marginBottom:16}}>
                <strong>Avertissements :</strong>
                <ul style={{margin:'8px 0 0', paddingLeft:20}}>
                  {preview.warnings.map((w, i) => <li key={i} style={{fontSize:12}}>{w}</li>)}
                </ul>
              </div>
            )}

            <div style={{maxHeight:300, overflowY:'auto', marginBottom:16}}>
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Pôle</th>
                    <th className="num">Payants</th>
                    <th className="num">Gratuits</th>
                    <th className="num">CA TTC</th>
                    <th>Superviseur</th>
                    <th>Accueil</th>
                    <th>En base</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.jours.map(j => (
                    <tr key={`${j.date}_${j.pole}`} style={!j.superviseurObj ? {background:'#FFF3F3'} : {}}>
                      <td>{new Date(j.date).toLocaleDateString('fr-FR')}{j.partielle && <span title="Date de soin hors de la période de paiement du fichier : probablement incomplète"> ⚠</span>}</td>
                      <td><span className={j.pole === 'HYDRO' ? 'badge badge-hydro' : 'badge badge-spa'}>{j.pole}</span></td>
                      <td className="num">{j.payants}</td>
                      <td className="num">{j.gratuits}{j.valorisation > 0 && <div style={{fontSize:11, color:'var(--stone)'}}>dont JPO valorisées {j.valorisation.toFixed(2)} €</div>}</td>
                      <td className="num">{j.ca.toFixed(2)} €</td>
                      <td>
                        {j.superviseurObj
                          ? <span style={{color:'var(--success)'}}>{j.superviseurObj.prenom} {j.superviseurObj.nom}</span>
                          : <span style={{color:'var(--error)'}}>{j.superviseurNom} ⚠</span>}
                      </td>
                      <td>
                        {j.accueilObj
                          ? <span style={{color:'var(--success)', fontSize:12}}>{j.accueilObj.prenom}</span>
                          : <span style={{color:'var(--stone)', fontSize:12}}>{j.accueilNom || '—'}</span>}
                      </td>
                      <td style={{fontSize:12}}>
                        {j.existante
                          ? (j.existante.cloture
                              ? <span style={{color:'var(--stone)'}}>clôturée, ignorée</span>
                              : <span style={{color:'var(--error)'}}>{j.existante.total_massage_payant + j.existante.total_massage_gratuit} soins, {Number(j.existante.ca_ttc).toFixed(2)} € : écrasée</span>)
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {progress && (
              <div className="alert alert-info">
                Import en cours… {progress.current}/{progress.total} — {new Date(progress.date).toLocaleDateString('fr-FR')}
              </div>
            )}

            <div style={{display:'flex', gap:10, justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={() => { setPreview(null); setFile(null) }}>
                Changer de fichier
              </button>
              <button className="btn btn-bordeaux" onClick={handleImport} disabled={importing}>
                {importing ? 'Import en cours...' : `Importer ${preview.jours.length} jour${preview.jours.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {msg && (
          <div style={{display:'flex', justifyContent:'flex-end', marginTop:16}}>
            <button className="btn btn-primary" onClick={onClose}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  )
}
